/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  ListSubheader,
  Paper,
  Popper,
  TextField,
  Tooltip,
} from '@material-ui/core'
import styled from '@emotion/styled'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import CircularProgressIcon from '@material-ui/core/CircularProgress'
import { useDebounce } from 'use-debounce'
import { useLazyQuery } from '@apollo/client'
import { getOrganizerProfile } from '../helpers/profileUtils'
import { filterTags, hexIsLight } from '../lib/constants'
import { Pencil } from './Icons/Pencil'
import Search from './Icons/Search'

import { GET_TAGS } from './graphql/query/getTags'

const StyledTextField = styled(TextField)`
  input {
    padding: 7px 12px;
    font-size: 14px;
  }
  .MuiOutlinedInput-notchedOutline {
    color: #c4cdd5;
  }
  .MuiInputAdornment-root {
    .MuiTypography-root {
      font-size: 14px;
    }
  }
  .Mui-focused {
    border: none;
    :not(.Mui-error) {
      .MuiOutlinedInput-notchedOutline {
        border: 1px solid #00848e !important;
        box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%),
          0 0 8px rgb(102 175 233 / 60%);
      }
    }
  }
`

const Dropdown = styled(Box)`
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px;
  flex-wrap: wrap;
  min-height: 39px;
  padding-right: 30px;
  position: relative;
  cursor: pointer;
  width: 100%;
`

const IconDown = styled(ArrowDropDownIcon)`
  position: absolute;
  right: 5px;
  color: rgba(0, 0, 0, 0.54);
  cursor: pointer;
`

const ColorItem = styled.div(
  ({ bgcolor, txtcolor }) => `
  background-color: ${bgcolor};
  width: fit-content;
  padding: 9px 25px;
  border-radius: 2em;
  color: ${txtcolor};
  font-size: 12px;
  font-family: 'Roboto';
  max-width: 90px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`,
)

const ChipCate = styled(Chip)(
  ({ bgcolor, txtcolor }) => `
  max-width: 150px;
  background-color: ${bgcolor};
  color: ${txtcolor};
`,
)

const Placeholder = styled.div`
  color: rgba(0, 0, 0, 0.4);
  padding-left: 7px;
`

const TagPopper = styled(Popper)`
  z-index: 1400;
`

const Wrapper = styled.div`
  width: 300px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0px 3px 30px 0px rgba(0, 0, 0, 0.15);
`

const DropdownBody = styled(Paper)`
  position: relative !important;
  transform: none;
  width: 300px !important;
  &.MuiAutocomplete-popper,
  .MuiAutocomplete-paper {
    box-shadow: none;
    transition: none;
    border-radius: 0;
  }
  .MuiAutocomplete-groupUl li {
    padding: 6px 16px;
  }
  .MuiAutocomplete-listbox {
    padding: 0;
    max-height: 35vh;
    ::-webkit-scrollbar {
      width: 20px;
      height: 18px;
    }
    ::-webkit-scrollbar-thumb {
      height: 6px;
      width: 12px;
      border: 5px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
      -webkit-border-radius: 15px;
      background-color: #dddddd;
      -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05),
        inset 1px 1px 0px rgba(0, 0, 0, 0.05);
    }
    ::-webkit-scrollbar-button {
      width: 0;
      height: 0;
      display: none;
    }
    ::-webkit-scrollbar-corner {
      background-color: transparent;
    }
    .MuiListSubheader-root {
      background-color: #fff;
    }
  }
`

const AutoTags = styled(Autocomplete)`
  padding: 15px 12px 0 12px;
`

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 12px;
`

const FlexDiv = styled.div`
  border-top: 1px solid #c4cdd5;
  padding: 0 19px;
  display: flex;
  align-items: center;
  color: #212b36;
  justify-content: space-between;
`

const renderGroup = (params) => [
  <ListSubheader key={params.key} component="div">
    {params.group ? params.group : <i>Uncategorized</i>}
  </ListSubheader>,
  params.children,
]

export const TagDropdown = ({
  departmentId,
  disabled,
  className,
  tags,
  setTags,
  type,
  placeholder,
}) => {
  const [defaultOptions, setDefaultOptions] = useState([])
  const [options, setOptions] = useState([])
  const [organizerId, setOrganizerId] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [search] = useDebounce(searchText, 500)

  const txtColor = (color) => (hexIsLight(color) ? '#000' : '#fff')
  const onClick = (event) => {
    if (disabled) {
      return
    }
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const [getTags, { loading }] = useLazyQuery(GET_TAGS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: ({ currentUser, tags: res }) => {
      setOptions(res)
      setDefaultOptions(res)
      setOrganizerId(getOrganizerProfile({ profiles: currentUser.profiles })?.organizer?.organizerId)
    },
  })

  useEffect(() => {
    if (anchorEl) {
      getTags(departmentId ? { variables: { departmentId } } : null)
    }
  }, [anchorEl, departmentId, getTags])

  useEffect(() => {
    setOptions(filterTags({ array: defaultOptions, search }))
  }, [defaultOptions, search])

  const onClose = () => {
    if (anchorEl) {
      anchorEl.focus()
    }
    setAnchorEl(null)
    setSearchText('')
  }

  const onDelete = (id) => {
    setTags(tags.filter((tag) => tag.id !== id))
  }

  const onEdit = () => {
    if (!organizerId) return
    window.open(`/organizers/${organizerId}/tags`, '_blank')
  }

  return (
    <div className={className}>
      <Dropdown className="tagDropdown" component="div" onClick={onClick}>
        {tags.length > 0 ? (
          tags.map((tag) => (
            <Tooltip key={tag.id} title={tag.name}>
              <ChipCate
                txtcolor={txtColor(tag.color)}
                bgcolor={tag.color}
                label={tag.name}
                onDelete={() => onDelete(tag.id)}
              />
            </Tooltip>
          ))
        ) : (
          <Placeholder className="placeholder">
            {placeholder || (
              <>
                {type === 'staff'
                  ? 'Select a tag to assign'
                  : 'Only users with these tags can see these shifts'}
              </>
            )}
          </Placeholder>
        )}
        <IconDown />
      </Dropdown>
      <TagPopper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={onClose}>
          <Wrapper>
            <AutoTags
              open
              fullWidth
              multiple
              PopperComponent={DropdownBody}
              value={tags}
              onChange={(event, newValue) => {
                setTags(newValue)
              }}
              inputValue={searchText}
              onInputChange={(_event, value, reason) => {
                if (reason !== 'reset') {
                  setSearchText(value)
                }
              }}
              disableCloseOnSelect
              disablePortal
              disabled
              loading={loading}
              renderTags={() => null}
              options={options}
              filterOptions={(opts) => opts}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, val) => option.name === val.name}
              groupBy={(option) => option.category}
              renderGroup={renderGroup}
              renderOption={(props, option) => (
                <li {...props}>
                  <DropdownItem>
                    <Tooltip title={option.name} placement="right">
                      <ColorItem
                        txtcolor={txtColor(option.color)}
                        bgcolor={option.color}
                      >
                        {option.name}
                      </ColorItem>
                    </Tooltip>
                    <div>{`applied to ${option.staffCount} staff`}</div>
                  </DropdownItem>
                </li>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Search"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgressIcon /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  autoFocus
                />
              )}
            />
            <FlexDiv>
              <div>Manage Labels</div>
              <IconButton disabled={!organizerId} onClick={onEdit}>
                <Pencil />
              </IconButton>
            </FlexDiv>
          </Wrapper>
        </ClickAwayListener>
      </TagPopper>
    </div>
  )
}
